import { useParams, Link } from "react-router-dom";
import {
  Server as ServerIcon, ArrowLeft, RefreshCw, MapPin, Cpu, Circle, Activity, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import {
  StatusBadge, MetricChart, Panel, DataTable, LogViewer, AsyncBoundary, UsageBar, EmptyState,
} from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { TONES, usageTone } from "@/lib/status";

const Gauge = ({ label, pct, sub }) => {
  const known = pct != null && Number.isFinite(pct);
  const tone = TONES[usageTone(known ? pct : 0)];
  return (
    <div className="rounded-xl border border-line bg-surface p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={cn("font-heading text-2xl font-bold", known ? tone.text : "text-muted-foreground")}>{known ? `${pct}%` : "—"}</span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-white/5">
        <div className={cn("h-full rounded-full", tone.dot)} style={{ width: `${known ? pct : 0}%` }} />
      </div>
      {sub && <p className="mt-2 font-mono text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
};

export default function ServerDetail() {
  const { id } = useParams();
  const { data, loading, error } = useAsync(() =>
    Promise.all([api.getServer(id), api.getServerExtras(id), api.getServerUsage(id), api.getContainers(), api.getLogs()])
      .then(([server, extras, usage, containers, logs]) => ({
        server, extras, usage,
        containers: containers.filter((c) => c.server === id),
        logs: logs.filter((l) => l.server === id || l.server === "—"),
      })),
  [id], 20000);

  const server = data?.server;

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/servidores" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="server-back">
        <ArrowLeft className="h-4 w-4" /> Voltar para Servidores
      </Link>

      <AsyncBoundary loading={loading} error={error}>
        {!server ? (
          <EmptyState icon={ServerIcon} title="Servidor não encontrado" description="Este nó não existe ou foi removido." />
        ) : (
          <>
            {/* Header */}
            <div className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className={cn("flex h-14 w-14 items-center justify-center rounded-xl border", server.status === "error" ? "border-err/30 bg-err/10 text-err" : "border-line bg-white/[0.03] text-brand")}>
                  <ServerIcon className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="font-heading text-2xl font-bold text-foreground">{server.hostname}</h1>
                    <StatusBadge status={server.status} />
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{server.label}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="font-mono text-foreground/80">{server.ip}</span>
                    <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{server.region}</span>
                    <span className="inline-flex items-center gap-1"><Cpu className="h-3 w-3" />{server.cpuCores} vCPU</span>
                    <span>{server.os}</span>
                    <span>{server.provider}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" disabled title="Operação de servidor ainda não está disponível" data-testid="server-maintenance">Manutenção indisponível</Button>
                <Button variant="outline" disabled title="Reinício de servidor ainda não está disponível" data-testid="server-restart"><RefreshCw className="h-4 w-4" /> Reinício indisponível</Button>
              </div>
            </div>

            <Tabs defaultValue="overview">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-line bg-surface p-1">
                {["overview", "containers", "networks", "volumes", "logs", "backups"].map((t) => (
                  <TabsTrigger key={t} value={t} data-testid={`server-tab-${t}`} className="capitalize data-[state=active]:bg-brand data-[state=active]:text-white">
                    {{ overview: "Overview", containers: "Containers", networks: "Redes", volumes: "Volumes", logs: "Logs", backups: "Backups" }[t]}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Overview */}
              <TabsContent value="overview" className="mt-5 space-y-5">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Gauge label="CPU" pct={server.cpuPct} sub={`Load ${server.load.join(" · ")}`} />
                  <Gauge label="RAM" pct={server.ramUsed != null && server.ramTotal ? Math.round((server.ramUsed / server.ramTotal) * 100) : null} sub={server.ramUsed != null && server.ramTotal != null ? `${server.ramUsed} / ${server.ramTotal} GB` : "Dados não coletados"} />
                  <Gauge label="Disco" pct={server.diskUsed != null && server.diskTotal ? Math.round((server.diskUsed / server.diskTotal) * 100) : null} sub={server.diskUsed != null && server.diskTotal != null ? `${server.diskUsed} / ${server.diskTotal} GB` : "Dados não coletados"} />
                </div>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
                  <Panel title="Uso de Recursos" icon={Activity} className="lg:col-span-2">
                    <p className="text-muted-foreground">Histórico ainda não coletado. As métricas acima são o último snapshot do Agent.</p>
                  </Panel>
                  <Panel title="Informações" className="lg:col-span-1" bodyClassName="space-y-3 text-sm">
                    {[
                      ["Uptime", server.uptime],
                      ["Agent", server.agentVersion || "—"],
                      ["Protocolo", server.protocolVersion || "—"],
                      ["Último contato", server.lastSeen ? new Date(server.lastSeen).toLocaleString() : "—"],
                      ["Containers", `${server.containers} descobertos`],
                      ["Rede (entrada)", server.net.in],
                      ["Rede (saída)", server.net.out],
                      ["Sistema", server.os],
                      ["Provedor", server.provider],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between border-b border-line/60 pb-2 last:border-0">
                        <span className="text-muted-foreground">{k}</span>
                        <span className="font-medium text-foreground">{v}</span>
                      </div>
                    ))}
                  </Panel>
                </div>
              </TabsContent>

              {/* Containers */}
              <TabsContent value="containers" className="mt-5">
                <DataTable
                  columns={[
                    { key: "name", header: "Nome", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
                    { key: "image", header: "Imagem", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.image}</span> },
                    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} size="sm" /> },
                    { key: "cpu", header: "CPU", align: "right", render: (r) => <span className="font-mono text-xs">{r.cpu}%</span> },
                    { key: "mem", header: "Memória", align: "right", render: (r) => <span className="font-mono text-xs">{r.memUsed} MB</span> },
                  ]}
                  rows={data.containers}
                  empty={<EmptyState icon={ServerIcon} title="Nenhum container" description="Este servidor ainda não hospeda containers." />}
                />
              </TabsContent>

              {/* Networks */}
              <TabsContent value="networks" className="mt-5">
                <DataTable
                  columns={[
                    { key: "name", header: "Rede", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
                    { key: "driver", header: "Driver" },
                    { key: "scope", header: "Escopo" },
                    { key: "subnet", header: "Subnet", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.subnet}</span> },
                    { key: "containers", header: "Containers", align: "right" },
                  ]}
                  rows={data.extras.networks}
                />
              </TabsContent>

              {/* Volumes */}
              <TabsContent value="volumes" className="mt-5">
                <DataTable
                  columns={[
                    { key: "name", header: "Volume", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
                    { key: "mount", header: "Mount", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.mount}</span> },
                    { key: "size", header: "Tamanho", align: "right" },
                    { key: "used", header: "Uso", tdClassName: "w-40", render: (r) => <span>Não coletado</span> },
                  ]}
                  rows={data.extras.volumes}
                />
              </TabsContent>

              {/* Logs */}
              <TabsContent value="logs" className="mt-5">
                <p>Logs do servidor ainda não implementados.</p>
              </TabsContent>

              {/* Backups */}
              <TabsContent value="backups" className="mt-5">
                <DataTable
                  columns={[
                    { key: "id", header: "ID", render: (r) => <span className="font-mono text-xs">{r.id}</span> },
                    { key: "target", header: "Alvo", render: (r) => <span className="font-medium text-foreground">{r.target}</span> },
                    { key: "type", header: "Tipo" },
                    { key: "size", header: "Tamanho", align: "right" },
                    { key: "when", header: "Quando" },
                    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} size="sm" /> },
                  ]}
                  rows={data.extras.backups} empty={<EmptyState title="Backups ainda não implementados"/>}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </AsyncBoundary>

    </div>
  );
}
